import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Chat as ChatIcon,
  People as PeopleIcon,
  Analytics as AnalyticsIcon,
  AccountCircle,
  ExitToApp,
  Settings as SettingsIcon,
  MenuBook as MenuBookIcon,
  NewReleases as ReleaseIcon,
  Add as AddIcon,
  Speed as SpeedIcon,
  TextSnippet as TemplateIcon,
  MeetingRoom as RoomManageIcon,
  FolderOpen as FileManageIcon,
  Security as AuditLogIcon,
  Api as ExternalApiIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePermissions } from './RoleBasedRoute';
import { PERMISSIONS } from '../types';

const DRAWER_WIDTH = 240;

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { adminUser, hasPermission, isAdmin } = usePermissions();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/login');
  };

  // ナビゲーションメニュー項目
  const menuItems = [
    {
      text: 'ダッシュボード',
      icon: <DashboardIcon />,
      path: '/admin/dashboard',
      permission: null,
    },
    {
      text: 'アカウント管理',
      icon: <PeopleIcon />,
      path: '/admin/accounts',
      permission: PERMISSIONS.ACCOUNT_MANAGEMENT,
    },
    {
      text: '分析・レポート',
      icon: <AnalyticsIcon />,
      path: '/admin/analytics',
      permission: PERMISSIONS.ANALYTICS_VIEW,
    },
    {
      text: 'システム設定',
      icon: <SettingsIcon />,
      path: '/admin/settings',
      permission: PERMISSIONS.SYSTEM_SETTINGS,
    },
    {
      text: 'ルーム管理',
      icon: <RoomManageIcon />,
      path: '/admin/rooms',
      permission: null,
    },
    {
      text: 'ファイル管理',
      icon: <FileManageIcon />,
      path: '/admin/files',
      permission: PERMISSIONS.SYSTEM_SETTINGS,
    },
    {
      text: 'テンプレート管理',
      icon: <TemplateIcon />,
      path: '/admin/templates',
      permission: null,
    },
    {
      text: 'レスポンスタイム（仮）',
      icon: <SpeedIcon />,
      path: '/admin/response-time',
      permission: PERMISSIONS.SYSTEM_SETTINGS,
    },
    {
      text: '監査ログ',
      icon: <AuditLogIcon />,
      path: '/admin/audit-logs',
      permission: PERMISSIONS.SYSTEM_SETTINGS,
    },
    {
      text: '外部API連携',
      icon: <ExternalApiIcon />,
      path: '/admin/external-api',
      permission: PERMISSIONS.SYSTEM_SETTINGS,
    },
    {
      text: 'ヘルプ・マニュアル（仮）',
      icon: <MenuBookIcon />,
      path: '/admin/help',
      permission: null,
    },
    {
      text: '更新情報',
      icon: <ReleaseIcon />,
      path: '/admin/release-notes',
      permission: null,
    },
  ];

  // 権限に基づいてメニューをフィルタリング
  const filteredMenuItems = menuItems.filter(item => {
    if (!item.permission) return true;
    // 管理者は権限に関わらず表示
    if (isAdmin()) return true;
    return hasPermission(item.permission);
  });

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return '管理者';
      case 'operator': return '運営者';
      default: return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'operator': return 'primary';
      default: return 'default';
    }
  };

  // サイドバーコンテンツ
  const drawer = (
    <Box>
      <Toolbar sx={{ justifyContent: 'center', py: 1.5 }}>
        <Box
          component="img"
          src="/logo-horizontal.png"
          alt="myTalk"
          sx={{ height: 40 }}
        />
      </Toolbar>
      <Divider />
      <List>
        {filteredMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            {item.text === 'ダッシュボード' ? (
              <ListItemButton component="a" href="/">
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            ) : (
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            )}
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {adminUser?.displayName || 'ユーザー'}さん - {getRoleLabel(adminUser?.role || '')}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={getRoleLabel(adminUser?.role || '')}
              color={getRoleColor(adminUser?.role || '') as any}
              size="small"
            />
            <IconButton
              size="large"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem disabled>
              <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                {adminUser?.displayName?.charAt(0)}
              </Avatar>
              {adminUser?.displayName} ({getRoleLabel(adminUser?.role || '')})
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ExitToApp sx={{ mr: 1 }} />
              ログアウト
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}; 